import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Avatar, Button, Chip, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Divider
} from '@mui/material';
import {
  Check, Close, Visibility, Language as LanguageIcon, School
} from '@mui/icons-material';
import { collection, getDocs, query, where, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../api/config';

const TeacherApproval = () => {
  const [pendingTeachers, setPendingTeachers] = useState([]);
  const [approvedTeachers, setApprovedTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeachers = async () => {
    try {
      const q = query(collection(db, COLLECTIONS.USERS), where('role', '==', 'teacher'));
      const snapshot = await getDocs(q);
      const teachers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      setPendingTeachers(teachers.filter(t => t.profileApproved !== true));
      setApprovedTeachers(teachers.filter(t => t.profileApproved === true));
    } catch (err) {
      console.error('Error loading teachers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (teacherId) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, teacherId), {
        profileApproved: true,
        approvedAt: serverTimestamp()
      });
      await loadTeachers();
      setSelectedTeacher(null);
    } catch (err) {
      console.error('Error approving:', err);
    }
  };

  const handleReject = async (teacherId) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, teacherId), {
        profileApproved: false,
        profileCompleted: false,
        rejectionReason: rejectReason
      });
      await loadTeachers();
      setSelectedTeacher(null);
      setRejectReason('');
    } catch (err) {
      console.error('Error rejecting:', err);
    }
  };

  const handleRevoke = async (teacherId) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, teacherId), {
        profileApproved: false
      });
      await loadTeachers();
    } catch (err) {
      console.error('Error revoking:', err);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const TeacherCard = ({ teacher, showActions }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Avatar src={teacher.photoURL} sx={{ width: 64, height: 64 }}>
            {teacher.displayName?.charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>{teacher.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">{teacher.email}</Typography>
            <Typography variant="body2" color="text.secondary">{teacher.country}</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {teacher.languages?.map(l => (
                <Chip key={l} label={l} size="small" icon={<LanguageIcon />} variant="outlined" />
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {teacher.specialties?.map(s => (
                <Chip key={s} label={s} size="small" icon={<School />} color="primary" variant="outlined" />
              ))}
            </Box>

            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Price:</strong> ${teacher.pricePerHour}/hr · <strong>Trial:</strong> ${teacher.trialPrice}
            </Typography>

            {teacher.bio && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {teacher.bio}
              </Typography>
            )}
          </Box>
        </Box>

        {showActions && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained" color="success" startIcon={<Check />}
              onClick={() => handleApprove(teacher.id)}
            >
              Approve
            </Button>
            <Button
              variant="outlined" color="error" startIcon={<Close />}
              onClick={() => { setSelectedTeacher(teacher); }}
            >
              Reject
            </Button>
            <Button
              variant="text" startIcon={<Visibility />}
              onClick={() => setSelectedTeacher(teacher)}
            >
              View Details
            </Button>
          </Box>
        )}

        {!showActions && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Chip label="Approved" color="success" size="small" />
            <Button size="small" color="error" onClick={() => handleRevoke(teacher.id)}>
              Revoke Approval
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Pending Approvals */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Pending Approval ({pendingTeachers.length})
      </Typography>

      {pendingTeachers.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>No teachers pending approval</Alert>
      ) : (
        pendingTeachers.map(t => <TeacherCard key={t.id} teacher={t} showActions />)
      )}

      <Divider sx={{ my: 3 }} />

      {/* Approved Teachers */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Approved Teachers ({approvedTeachers.length})
      </Typography>

      {approvedTeachers.length === 0 ? (
        <Alert severity="info">No approved teachers yet</Alert>
      ) : (
        approvedTeachers.map(t => <TeacherCard key={t.id} teacher={t} showActions={false} />)
      )}

      {/* Reject Dialog */}
      <Dialog open={!!selectedTeacher} onClose={() => setSelectedTeacher(null)} maxWidth="sm" fullWidth>
        {selectedTeacher && (
          <>
            <DialogTitle>Teacher Profile: {selectedTeacher.displayName}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600}>Bio</Typography>
                  <Typography variant="body2">{selectedTeacher.bio || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600}>Description</Typography>
                  <Typography variant="body2">{selectedTeacher.description || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600}>Experience</Typography>
                  <Typography variant="body2">{selectedTeacher.experience || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600}>Education</Typography>
                  <Typography variant="body2">{selectedTeacher.education || 'N/A'}</Typography>
                </Grid>
                {selectedTeacher.certifications?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600}>Certifications</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selectedTeacher.certifications.map((c, i) => (
                        <Chip key={i} label={c} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Rejection Reason (if rejecting)"
                    multiline rows={2} value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedTeacher(null)}>Close</Button>
              {!selectedTeacher.profileApproved && (
                <>
                  <Button
                    color="error" onClick={() => handleReject(selectedTeacher.id)}
                    disabled={!rejectReason}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained" color="success"
                    onClick={() => handleApprove(selectedTeacher.id)}
                  >
                    Approve
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TeacherApproval;
